import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { AccountResponse } from '../../models/account/AccountResponse';
import { ProjectResponse } from '../../models/projects/ProjectResponse';
import useDataLayer, { IUserDataDataLayer } from '../../hooks/global/UseDataLayer';
import { getAccount } from '../../services/account/AccountService';
import { getProjects } from '../../services/projects/ProjectService';
import { trackIntercomEventOnce } from '../../services/intercom/trackEvent';
import { CONTEXT_ANALYSIS, GEO_CHECKLIST, PROMPTS, SOURCES } from '../../routes/path';
import { setModelAliasEnabled } from '../../utils/modelUrlAlias';
import { setAccountBannedHandler } from '../../services/apiClient';

interface AccountContextType {
  accountResponse: AccountResponse | null;
  setAccountResponse: Dispatch<SetStateAction<AccountResponse | null>>;
  projectResponse: ProjectResponse[] | null;
  setProjectResponse: Dispatch<SetStateAction<ProjectResponse[] | null>>;
  showOnboarding: boolean;
  setShowOnboarding: Dispatch<SetStateAction<boolean>>;
  showVideoTutorial: boolean;
  setShowVideoTutorial: Dispatch<SetStateAction<boolean>>;
  refetchData: () => void;
  refetchAccountSilently: () => Promise<void>;
  refetchProjectsSilently: () => Promise<void>;
}

export const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const AccountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
  const [showVideoTutorial, setShowVideoTutorial] = useState<boolean>(false);
  const [accountResponse, setAccountResponse] = useState<AccountResponse | null>(null);
  const [projectResponse, setProjectResponse] = useState<ProjectResponse[] | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);

  const { pushDataLayer } = useDataLayer();
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  const refetchData = useCallback(() => {
    setAccountResponse(null);
    setProjectResponse(null);
    setRefetchTrigger((prev) => prev + 1);
  }, []);

  const refetchAccountSilently = useCallback(async () => {
    try {
      const freshAccount = await getAccount();
      if (freshAccount) {
        setAccountResponse(freshAccount);
      }
    } catch (error) {
      console.error('Silent account refetch failed:', error);
    }
  }, []);

  const refetchProjectsSilently = useCallback(async () => {
    try {
      const freshProjects = await getProjects();
      if (freshProjects) {
        setProjectResponse(freshProjects);
      }
    } catch (error) {
      console.error('Silent projects refetch failed:', error);
    }
  }, []);

  // Send data layer event when location changes and user is authenticated
  useEffect(() => {
    if (accountResponse && location.pathname !== lastPath.current) {
      pushDataLayer<IUserDataDataLayer>({
        event: 'user_data',
        email_hashed: accountResponse.email,
        user_id: accountResponse.userId?.toString(),
        account_type: accountResponse.accountType,
        account_id: accountResponse.accountId.toString(),
      });
      lastPath.current = location.pathname;
    }
  }, [location.pathname, accountResponse, pushDataLayer]);

  // Kodowanie nazw modeli w URL-ach żądań włączamy dopiero, gdy wiemy, że konto ma addon
  // `MODEL_URL_ALIAS` — `apiClient` żyje poza drzewem Reacta, więc dostaje tę informację przez setter.
  useEffect(() => {
    setModelAliasEnabled(accountResponse?.addons?.includes('MODEL_URL_ALIAS') ?? false);
  }, [accountResponse]);

  // 403 `Account banned` on any request → refetch; `isBanned` then routes to BannedPage.
  useEffect(() => {
    setAccountBannedHandler(refetchAccountSilently);
    return () => setAccountBannedHandler(null);
  }, [refetchAccountSilently]);

  // Onboarding checklist (Intercom): fire a one-time event when the user first
  // opens each tracked section, powering the "visit" steps of the checklist.
  // Guarded per user so re-navigating does not inflate the event count.
  useEffect(() => {
    // `/projects/:id` is the AI-visibility dashboard; the regex excludes the
    // `/projects/add` creation wizard and the bare `/projects` list.
    if (/^\/projects\/\d+/.test(location.pathname)) {
      trackIntercomEventOnce(accountResponse, 'onboarding_dashboard_visited');
    }
    if (location.pathname.startsWith(PROMPTS)) {
      // "Review your Suggested Prompts" step — visiting the prompts page is the
      // agreed trigger (was: adding a suggested prompt).
      trackIntercomEventOnce(accountResponse, 'onboarding_prompts_reviewed');
    }
    if (location.pathname.startsWith(CONTEXT_ANALYSIS)) {
      trackIntercomEventOnce(accountResponse, 'onboarding_context_opened');
    }
    if (location.pathname.startsWith(GEO_CHECKLIST)) {
      // "Complete your first GEO action" step — viewing the recommendations page
      // is the agreed trigger (was: checking a task). Event name kept: the
      // Intercom checklist rule binds to it.
      trackIntercomEventOnce(accountResponse, 'onboarding_geo_action_completed');
    }
    if (location.pathname.startsWith(SOURCES)) {
      trackIntercomEventOnce(accountResponse, 'onboarding_sources_visited');
    }
  }, [location.pathname, accountResponse]);

  return (
    <AccountContext.Provider
      value={{
        showOnboarding,
        setShowOnboarding,
        showVideoTutorial,
        setShowVideoTutorial,
        accountResponse,
        setAccountResponse,
        projectResponse,
        setProjectResponse,
        refetchData,
        refetchAccountSilently,
        refetchProjectsSilently,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw Error('useAccount must be used with an AccountProvider');
  }

  return context;
};
